import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBusiness } from '../contexts/BusinessContext';

// Drawer width for calculations
const drawerWidth = 256;
const collapsedDrawerWidth = 72;

export default function AdminLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isPlatformAdmin } = useAuth();
  const { businesses, selectedBusiness, selectBusiness, loading } = useBusiness();
  
  // State
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  
  // Toggle drawer
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  // Handle user menu
  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };
  
  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };
  
  // Handle business change
  const handleBusinessChange = (event) => {
    selectBusiness(event.target.value);
  };
  
  // Navigation items
  const mainNavItems = [
    { path: '/dashboard', label: 'Dashboard', exact: true, icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"></path>
        <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"></path>
      </svg>
    ), adminOnly: true },
    { path: '/businesses', label: 'Businesses', icon: (
      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd"></path>
      </svg>
    ) }
  ];
  
  // Filter navigation items based on user role
  const filteredMainNavItems = mainNavItems.filter(item => 
    !item.adminOnly || isPlatformAdmin
  );
  
  // Helper function to check if a nav item should be active
  const isNavItemActive = (item) => {
    if (item.exact) {
      // For exact matches, like dashboard
      return location.pathname === item.path || location.pathname === '/' && item.path === '/dashboard';
    } else {
      // For partial matches like /businesses and /businesses/123
      return location.pathname.startsWith(item.path);
    }
  };
  
  // Drawer content
  const drawerContent = (
    <div className="h-full bg-dark-400 text-white">
      <div className="flex items-center justify-between p-4">
        <Link to="/" className="text-primary-400 font-bold text-lg">
          Voice Order Admin
        </Link>
        <button 
          onClick={toggleDrawer}
          className="hidden md:flex text-gray-400 hover:text-white p-1 rounded"
        >
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd"></path>
          </svg>
        </button>
      </div>
      
      <div className="border-b border-dark-600"></div>
      
      {/* Business selector removed - users will select businesses from the Businesses page */}
      
      {/* Main navigation */}
      <nav className="mt-2">
        <ul className="px-2 space-y-1">
          {filteredMainNavItems.map((item) => (
            <li key={item.path}>
              <Link 
                to={item.path}
                className={`flex items-center px-3 py-2 rounded-md ${
                  isNavItemActive(item)
                    ? 'bg-primary-900 text-primary-400'
                    : 'text-gray-300 hover:bg-dark-700 hover:text-white'
                }`}
              >
                <span className={`mr-3 ${
                  isNavItemActive(item) ? 'text-primary-400' : 'text-gray-400'
                }`}>
                  {item.icon}
                </span>
                {drawerOpen && <span>{item.label}</span>}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
      
      <div className="border-b border-dark-600 my-3"></div>
      
      {/* No business management section anymore - everything is in the business detail page */}
    </div>
  );
  
  return (
    <div className="flex h-screen bg-dark-800">
      {/* Mobile drawer backdrop */}
      {mobileDrawerOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-10 md:hidden"
          onClick={() => setMobileDrawerOpen(false)}
        ></div>
      )}
      
      {/* Mobile drawer */}
      <div 
        className={`fixed inset-y-0 left-0 w-64 bg-dark-800 transform ${
          mobileDrawerOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-300 ease-in-out z-20 md:hidden`}
      >
        {drawerContent}
      </div>
      
      {/* Desktop drawer */}
      <div 
        className="hidden md:block"
        style={{
          width: drawerOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px`,
          minWidth: drawerOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px`,
          flexShrink: 0,
          transition: 'width 0.3s, min-width 0.3s'
        }}
      >
        <div 
          className="fixed inset-y-0 overflow-y-auto"
          style={{
            width: drawerOpen ? `${drawerWidth}px` : `${collapsedDrawerWidth}px`,
            transition: 'width 0.3s'
          }}
        >
          {drawerContent}
        </div>
      </div>
      
      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar */}
        <header className="bg-dark-700 border-b border-dark-600 shadow-sm">
          <div className="flex items-center justify-between h-16 px-4">
            <div className="flex items-center">
              <button
                onClick={() => setMobileDrawerOpen(true)}
                className="text-gray-400 hover:text-white p-2 rounded-md md:hidden"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"></path>
                </svg>
              </button>
              
              <button
                onClick={toggleDrawer}
                className="hidden md:block text-gray-400 hover:text-white p-2 rounded-md"
              >
                <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd"></path>
                </svg>
              </button>
              
              <h1 className="ml-4 text-lg font-medium text-white hidden sm:block">
                {isPlatformAdmin && !location.pathname.includes('/businesses/') ? 'Voice Ordering Admin' : (selectedBusiness ? selectedBusiness.name : 'Voice Ordering Admin')}
              </h1>
            </div>
            
            {/* User menu */}
            <div className="relative">
              <button
                onClick={toggleUserMenu}
                className="flex items-center p-1"
              >
                <div className="w-8 h-8 rounded-full bg-primary-600 flex items-center justify-center text-white font-medium">
                  {user && user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </div>
              </button>
              
              {userMenuOpen && (
                <div className="absolute right-0 w-48 mt-2 py-2 bg-dark-700 border border-dark-600 rounded-lg shadow-xl z-50">
                  <div className="px-4 py-2">
                    <p className="text-sm font-medium text-white">{user && user.name ? user.name : 'User'}</p>
                    <p className="text-xs text-gray-400">
                      {isPlatformAdmin ? 'Platform Administrator' : 'Business Manager'}
                    </p>
                  </div>
                  
                  <div className="border-t border-dark-600 my-1"></div>
                  
                  <Link
                    to="/profile"
                    className="block px-4 py-2 text-sm text-gray-300 hover:bg-dark-600 hover:text-white"
                  >
                    Profile
                  </Link>
                  
                  <Link
                    to="/settings"
                    className="block px-4 py-2 text-sm text-gray-300 hover:bg-dark-600 hover:text-white"
                  >
                    Settings
                  </Link>
                  
                  <div className="border-t border-dark-600 my-1"></div>
                  
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-300 hover:bg-dark-600 hover:text-white"
                  >
                    <div className="flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                        <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd"></path>
                      </svg>
                      <span>Logout</span>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>
        
        {/* Page content */}
        <main 
          className="flex-1 overflow-y-auto p-4 bg-dark-800"
          style={{
            width: '100%',
            marginLeft: 0,
            transition: 'all 0.3s'
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}